import { useI18n, type Locale } from '../i18n';
import { api } from '../api';

export function Landing({ onDemo }: { onDemo: () => void }) {
  const { t, locale, setLocale } = useI18n();

  const signIn = () => {
    window.location.href = api.yandexSignInUrl();
  };

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-brand">
          <div className="sidebar-brand-logo" />
          <div>
            <div className="sidebar-brand-name">{t('brand.name')}</div>
            <div className="sidebar-brand-tag">{t('brand.tagline')}</div>
          </div>
        </div>
        <div className="landing-nav-right">
          <div className="lang-toggle">
            {(['ru', 'en'] as Locale[]).map((l) => (
              <button
                key={l}
                className={`lang-pill ${locale === l ? 'active' : ''}`}
                onClick={() => setLocale(l)}
                type="button"
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={signIn}>
            {t('auth.signin')}
          </button>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-badge">{t('landing.badge')}</div>
        <h1 className="landing-title">
          {t('landing.hero.title').split('\n').map((line, i) => (
            <span key={i}>
              {line}
              <br />
            </span>
          ))}
        </h1>
        <p className="landing-sub">{t('landing.hero.sub')}</p>
        <div className="landing-cta-row">
          <button className="btn btn-primary btn-lg" onClick={signIn}>
            {t('landing.cta.primary')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={onDemo}>
            {t('landing.cta.demo')}
          </button>
        </div>
        <div className="landing-cta-note">{t('landing.cta.note')}</div>
      </section>

      <section className="landing-section">
        <h2 className="landing-section-title">{t('landing.features.title')}</h2>
        <div className="landing-features">
          {[
            { t: 'landing.feat.capture.title', b: 'landing.feat.capture.body', icon: '⌗' },
            { t: 'landing.feat.plan.title', b: 'landing.feat.plan.body', icon: '◎' },
            { t: 'landing.feat.decide.title', b: 'landing.feat.decide.body', icon: '◇' },
            { t: 'landing.feat.daily.title', b: 'landing.feat.daily.body', icon: '◷' },
          ].map((f) => (
            <div key={f.t} className="landing-feature">
              <div className="landing-feature-icon">{f.icon}</div>
              <div className="landing-feature-title">{t(f.t)}</div>
              <div className="landing-feature-body">{t(f.b)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <h2 className="landing-section-title">{t('landing.pricing.title')}</h2>
        <div className="landing-pricing-note">{t('landing.pricing.note')}</div>
        <div className="landing-plans">
          <Plan
            title={t('landing.plan.free.title')}
            price={t('landing.plan.free.price')}
            period={t('landing.plan.free.period')}
            features={[
              t('landing.plan.free.f1'),
              t('landing.plan.free.f2'),
              t('landing.plan.free.f3'),
            ]}
            cta={t('landing.cta.primary')}
            onClick={signIn}
            highlight
          />
          <Plan
            title={t('landing.plan.pro.title')}
            price={t('landing.plan.pro.price')}
            period={t('landing.plan.pro.period')}
            badge={t('landing.plan.pro.badge')}
            features={[
              t('landing.plan.pro.f1'),
              t('landing.plan.pro.f2'),
              t('landing.plan.pro.f3'),
            ]}
            cta={t('landing.cta.primary')}
            onClick={signIn}
          />
          <Plan
            title={t('landing.plan.team.title')}
            price={t('landing.plan.team.price')}
            period={t('landing.plan.team.period')}
            badge={t('landing.plan.team.badge')}
            features={[
              t('landing.plan.team.f1'),
              t('landing.plan.team.f2'),
              t('landing.plan.team.f3'),
            ]}
            cta={t('landing.cta.primary')}
            onClick={signIn}
          />
        </div>
      </section>

      <footer className="landing-foot">
        <div>{t('landing.footer.privacy')}</div>
        <div>{t('landing.footer.region')}</div>
      </footer>
    </div>
  );
}

interface PlanProps {
  title: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  badge?: string;
  highlight?: boolean;
  onClick: () => void;
}

function Plan({ title, price, period, features, cta, badge, highlight, onClick }: PlanProps) {
  return (
    <div className={`landing-plan ${highlight ? 'highlight' : ''}`}>
      {badge && <div className="landing-plan-badge">{badge}</div>}
      <div className="landing-plan-title">{title}</div>
      <div className="landing-plan-price">
        <span>{price}</span>
        <span className="landing-plan-period">{period}</span>
      </div>
      <ul className="landing-plan-features">
        {features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <button className={`btn ${highlight ? 'btn-primary' : ''} landing-plan-cta`} onClick={onClick}>
        {cta}
      </button>
    </div>
  );
}
